import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Link,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DataTable from '../../../sharedModules/dataTable';
import { CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import { formatDollar, formatOptionalDate } from '../utils/format';
import { apiAbsoluteUrl } from '../utils/pdf';
import { useInvoicePdfUpload } from '../hooks/useInvoicePdfUpload';
import { groupInvoicesByDepartment } from '../utils/groupInvoices';

interface Props {
  documents: CollectionInvoiceDocument[];
  location: string;
  acctNo: string;
  onUploaded: (applyTo: string, pdf: CollectionInvoicePdf) => void;
  noCard?: boolean;
}

const compactTableStyles = {
  headRow: {
    style: {
      minHeight: '42px',
    },
  },
  headCells: {
    style: {
      paddingTop: '6px',
      paddingBottom: '6px',
    },
  },
  rows: {
    style: {
      minHeight: '40px',
    },
  },
  cells: {
    style: {
      paddingTop: '6px',
      paddingBottom: '6px',
    },
  },
};

const departmentAccordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
  boxShadow: 1,
  '&:before': { display: 'none' },
};

const buildColumns = (
  triggerUpload: (applyTo: string) => void,
  uploadingApplyTo: string | null,
  canUpload: boolean,
) => [
  {
    name: 'Invoice #',
    selector: (row: CollectionInvoiceDocument) => row.apply_to,
    width: '130px',
  },
  {
    name: 'Invoice Date',
    selector: (row: CollectionInvoiceDocument) => formatOptionalDate(row.invoice_date),
    width: '130px',
  },
  {
    name: 'Due Date',
    selector: (row: CollectionInvoiceDocument) => formatOptionalDate(row.due_date),
    width: '130px',
  },
  {
    name: 'Amount',
    selector: (row: CollectionInvoiceDocument) => row.amount,
    cell: (row: CollectionInvoiceDocument) => (
      <Typography variant="body2">{formatDollar(row.amount)}</Typography>
    ),
    right: true,
    width: '170px',
  },
  {
    name: 'PDF',
    selector: (row: CollectionInvoiceDocument) => row.pdf?.filename ?? '',
    grow: 2,
    cell: (row: CollectionInvoiceDocument) =>
      row.pdf ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PictureAsPdfIcon sx={{ color: '#e53935', fontSize: 18, flexShrink: 0 }} />
          {row.pdf.download_url ? (
            <Link
              href={apiAbsoluteUrl(row.pdf.download_url)}
              download={row.pdf.filename}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ wordBreak: 'break-all' }}
            >
              {row.pdf.filename}
            </Link>
          ) : (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {row.pdf.filename}
            </Typography>
          )}
        </Box>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          disabled={!canUpload || uploadingApplyTo === row.apply_to}
          onClick={() => triggerUpload(row.apply_to)}
        >
          {uploadingApplyTo === row.apply_to ? 'Uploading…' : 'Upload Invoice'}
        </Button>
      ),
  },
];

const ClientInvoicesTable: React.FC<Props> = ({ documents, location, acctNo, onUploaded, noCard = false }) => {
  const { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo, canUpload } = useInvoicePdfUpload({
    location,
    acctNo,
    onUploaded,
  });

  const columns = useMemo(
    () => buildColumns(triggerUpload, uploadingApplyTo, canUpload),
    [triggerUpload, uploadingApplyTo, canUpload],
  );

  const groups = useMemo(() => groupInvoicesByDepartment(documents), [documents]);
  const totalAmount = useMemo(() => documents.reduce((sum, invoice) => sum + invoice.amount, 0), [documents]);
  const namedDepartmentCount = useMemo(() => groups.filter((group) => !group.isUngrouped).length, [groups]);
  const hasUngroupedGroup = useMemo(() => groups.some((group) => group.isUngrouped), [groups]);

  const content =
    documents.length === 0 ? (
      <Box sx={{ p: 3, color: 'text.secondary' }}>No past-due invoices.</Box>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Card>
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                {documents.length}
              </Box>{' '}
              collection {documents.length === 1 ? 'invoice' : 'invoices'} in{' '}
              <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                {namedDepartmentCount}
              </Box>{' '}
              {namedDepartmentCount === 1 ? 'department' : 'departments'}
              {hasUngroupedGroup ? ' + ungrouped' : ''} with past due of{' '}
              <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
                {formatDollar(totalAmount)}
              </Box>
            </Typography>
          </Box>
        </Card>
        {groups.map((group) => (
          <Accordion key={group.label} disableGutters defaultExpanded={false} sx={departmentAccordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                minHeight: 48,
                '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 1 },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {group.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                · {group.count} {group.count === 1 ? 'invoice' : 'invoices'}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'error.main' }}>
                · {formatDollar(group.total)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <DataTable
                keyField="apply_to"
                columns={columns}
                data={group.invoices}
                highlightOnHover
                striped
                noHeader
                dense
                customStyles={compactTableStyles}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );

  return (
    <>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFileSelected} />
     {noCard ? <Box sx={{ p: 2 }}>{content}</Box> : content}
    </>
  );
};

export default ClientInvoicesTable;
